/**
 * Global Client-Side Error Logger
 * 
 * Captures and logs client-side errors to the database for debugging.
 * Includes console errors, unhandled promise rejections, and React errors.
 * 
 * Can be toggled on/off via admin settings without code deployment.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { isErrorTrackingEnabled } from './errorLoggerConfig';
import { matchesChunkLoadPattern } from './chunkLoadError';
import {
  INJECTED_SCRIPT_MESSAGE_PATTERNS,
  isThirdPartyOriginStack,
} from './thirdPartyOrigin';

/**
 * Captured at MODULE LOAD time, before Sentry.init() (deferred via
 * requestIdleCallback in instrument.ts) and before our own initErrorLogger()
 * wraps console.error. This is the raw browser console.error — calling
 * through this ref guarantees the message goes ONLY to DevTools, not through
 * Sentry's captureConsoleIntegration and not through our own wrapper.
 *
 * Why this matters: the error-logger's own internal failure paths (Supabase
 * insert blew up, exception in the try block) need to produce a developer-
 * visible message without re-entering either pipeline. The previous
 * implementation used naked `console.error('[ErrorLogger] Failed to log
 * error:', insertError)`, which Sentry's console integration captured as
 * its own event ("[ErrorLogger] Failed to log error: [object Object]" —
 * SPOTIVIBLY-WEB-V) with zero debugging value. The captured ref breaks the
 * recursion at the source.
 *
 * Andy 2026-05-11 audit: "prevent critical errors from happening by handling
 * them gracefully."
 */
const _rawConsoleError: (...args: unknown[]) => void =
  typeof console !== 'undefined' && typeof console.error === 'function'
    ? console.error.bind(console)
    : () => {};

interface ErrorLog {
  error_type: 'console_error' | 'unhandled_rejection' | 'react_error' | 'custom';
  message: string;
  stack?: string;
  url: string;
  user_agent: string;
  user_id?: string;
  metadata?: Record<string, unknown>;
}

// Rate limiting to prevent spam
const ERROR_LOG_LIMIT = 50; // Max errors per session
let errorCount = 0;
const loggedErrors = new Set<string>();
// Cache user ID to avoid network call on every error
let cachedUserId: string | null = null;
let userIdFetched = false;
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0"]);
const IGNORED_CONSOLE_PATTERNS = [
  "[usegenerationlimits] error:",
  "edge function returned a non-2xx status code",
  // Fire-and-forget telemetry inserts that adblock commonly blocks. Failure
  // has zero UX impact and floods client_errors with one row per blocked
  // visitor per page load.
  "[blogpost] engagement tracking failed",
  // Our own infrastructure meta-errors — these come from logger paths that
  // failed for the same reason the original error was being logged (network
  // dropout, Supabase outage). Logging them again accomplishes nothing.
  // Belt-and-braces with the _rawConsoleError fix above — even if a
  // [ErrorLogger] message somehow reaches us, we drop it here.
  "[errorlogger]",
  // 2026-08-19: ErrorBoundary.componentDidCatch calls console.error AND
  // logReactError, so every caught error wrote TWO rows — a `react_error`
  // and this `console_error` carrying the same failure plus a component
  // stack. 11 of the 39 rows in the client_errors export were the duplicate
  // halves of 11 incidents. The console.error call stays: it is what gives
  // Sentry a real exception with a stack (see the comment at its call site),
  // and Sentry filters through its own beforeSend, not this list. Only the
  // duplicate database row goes away.
  "[errorboundary] caught error:",
  // A browser layout notice, not an error. Fires when a ResizeObserver
  // callback resizes its own observed element, which the browser resolves by
  // deferring to the next frame; nothing is broken and nothing is actionable.
  // Covers both wordings ("loop limit exceeded" / "loop completed with
  // undelivered notifications").
  "resizeobserver loop",
];

// Post-deploy stale-chunk noise. ErrorBoundary reloads the page on the first
// occurrence and offers a Reload button after that (see
// ErrorBoundary.componentDidCatch), so logging it to client_errors adds
// nothing but burns storage.
//
// 2026-08-02 (S16-AUD-127): the pattern list moved to @/lib/chunkLoadError so
// this filter, the Sentry filter in src/instrument.ts and the ErrorBoundary
// branch share one literal instead of three hand-mirrored copies.
// design-rules.md §12.6 requires the first two to agree; sharing the module
// makes that structural rather than a review obligation.

// 2026-05-19 (SPOTIVIBLY-WEB-18): Supabase auth rate-limit responses logged
// via console.error and captured by errorLogger's console proxy. Code
// `over_request_rate_limit` / message "Request rate limit reached" /
// status 429 is Supabase legitimately throttling a user hammering sign-in
// or sign-up endpoints. Not actionable; the calling code handles the
// 429 with a user-facing toast.
const AUTH_RATE_LIMIT_MESSAGE_PATTERNS = [
  "request rate limit reached",
  "over_request_rate_limit",
];

// Third-party script patterns in stack traces — these are not our code
const THIRD_PARTY_STACK_PATTERNS = [
  "postuserdata",
  "postvisitordata",
];

// Host-suffixed network-blip pattern. Mirrors instrument.ts (Sentry filter)
// so the client_errors dashboard and Sentry agree on what's noise. Pattern:
// optional "typeerror: " prefix, "failed to fetch" or "load failed", then
// "(<known noisy host>)". Listed hosts are third-party SaaS we depend on
// where flaky-fetch errors are 99% transient user-side network issues, not
// actionable signals about our own code.
//
// 2026-07-15 (SPOTIVIBLY-WEB-1V): allows an app-context PREFIX and adds
// Firefox's "NetworkError when attempting to fetch resource." variant. See the
// full rationale in src/instrument.ts, where this literal is mirrored
// byte-identically (enforced by __tests__/noisy-hosts-regex-parity.test.ts).
// The `(^|: )` + `$` + host allowlist keep it to blips that are the TERMINAL
// cause; real save failures (RLS, 23505, 'permission denied for table') carry
// no host suffix and are still logged.
//
// 2026-07-18: added images.unsplash.com — "Load failed (images.unsplash.com)"
// unhandled_rejection from /my-vibe (decorative vibe-quiz images; the fetch
// came from an extension userscript, not our code). See instrument.ts.
const NOISY_THIRD_PARTY_HOSTS_RE =
  /(^|: )(typeerror: )?(failed to fetch|load failed|networkerror when attempting to fetch resource\.) \((tnffhgpavnrjozjjiasz\.supabase\.co|.*\.googleapis\.com|.*\.stripe\.com|js\.stripe\.com|.*\.youtube\.com|.*\.google-analytics\.com|.*\.googletagmanager\.com|.*\.googlesyndication\.com|.*\.doubleclick\.net|.*\.hotjar\.com|.*\.sentry\.io|images\.unsplash\.com|.*\.scdn\.co|.*\.spotifycdn\.com)\)$/;
// 2026-07-22: added googlesyndication.com + doubleclick.net. A Google Ads
// conversion tag (AW-*) fetches pagead2.googlesyndication.com, which ad
// blockers and privacy extensions block by definition — the observed report
// came through a Chrome extension that had wrapped window.fetch
// (injectScriptAdjust.js), so the rejection surfaced as an unhandled promise
// with OUR bundle in the stack. Nothing on our side can prevent or fix it,
// and the rate scales with ad-blocker adoption, not with anything we ship.
// MUST stay identical to the copy in src/instrument.ts (design-rules §12.6).

// Message patterns from browser extensions / third-party code we can't control
const THIRD_PARTY_MESSAGE_PATTERNS = [
  // Edge/crypto wallet extension noise (e.g. "Object Not Found Matching Id:3, MethodName:update, ParamCount:4")
  "object not found matching id",
  // Common browser extension errors
  "extension context invalidated",
  "the message port closed before a response was received",
  // 2026-04-30: third-party WebWorker scripts (analytics/fingerprinting/
  // bot-detection vendors) loading WASM from CDNs. Surfaces as "Failed to
  // execute 'importScripts' on 'WorkerGlobalScope': The script at
  // 'https://...cloudfront.net/.../foo.wasm.wasm' failed to load." — we
  // don't load WASM in our own workers. Either an extension (ad-blocker
  // breaking a tracker pixel) or a third-party tag whose CDN hiccupped.
  // The two patterns below uniquely fingerprint that scenario; we
  // intentionally do NOT match bare `cloudfront.net` because Supabase
  // Storage / Stripe.js / future CDN-fronted dependencies emit
  // `TypeError: Failed to fetch <cloudfront-url>` for legit network blips
  // that DO belong in the dashboard.
  "failed to execute 'importscripts'",
  ".wasm.wasm",
  // 2026-05-21: a browser extension on the user's page is calling
  // Sentry.init() in extension context. Sentry's own SDK detects this
  // and warns via console.error; captureConsoleIntegration picks it up
  // and re-routes it to errorLogger. Not actionable — the call is from
  // THEIR extension, not our app. Observed on both / (with OAuth
  // #access_token hash, authed user) and /spotify-playlist-creator (anon).
  "you cannot use sentry.init() in a browser extension",
  // 2026-05-13: Microsoft Clarity (clarity.ms) and similar tracking-pixel
  // libraries throw 'Invalid data: Key Name: event' when their internal
  // IndexedDB store rejects an event (quota / privacy-mode / out-of-sync
  // schema). We don't use IndexedDB in our own code — any occurrence is
  // from a third-party tracker. Observed in client_errors from anonymous
  // users on the homepage. Not actionable for us.
  "invalid data: key name: event",
  // 2026-05-26: injected third-party pixel scripts (ManagedScripts library —
  // Meta / Microsoft UET / Hotjar / Clarity / LinkedIn / TikTok / Reddit /
  // X) call their own fetch+JSON.parse on config endpoints. When Cloudflare
  // / pixel-vendor front returns an HTML interstitial (5xx, captcha) the
  // parse throws as an unhandled promise rejection from the dynamically-
  // evaluated VM module. Pattern is "Unexpected token '<', '<html..." or
  // "Unexpected token 'h', '<html><html..." depending on whitespace.
  // Our own JSON.parse calls are all wrapped in try/catch (verified
  // 2026-05-26) so any "<html" parse leak is third-party.
  "unexpected token '<'",
  "unexpected token \"<\"",
  "unexpected token 'h', \"<html",
  "unexpected token 'h', \" html><html",
  "<!doctype \"... is not valid json",
  "<html\"... is not valid json",
  // 2026-06-28 (Sentry batch): browser-extension / injected-script noise.
  // None of these symbols exist in our source (grep-verified). Mirrors the
  // instrument.ts extension block so Sentry + client_errors agree (§12.6).
  "invalid call to runtime.sendmessage",
  "refused to evaluate a string as javascript",
  "standardselectors",
  "can't find variable: gmo",
  // 2026-08-19: injected-script collisions ("Identifier 'initCoreHelpers' has
  // already been declared"). Stackless, so the origin check below cannot see
  // them. Shared with instrument.ts.
  ...INJECTED_SCRIPT_MESSAGE_PATTERNS,
];

function shouldSkipClientError(error: ErrorLog): boolean {
  try {
    const hostname = new URL(error.url).hostname.toLowerCase();
    if (LOCAL_HOSTNAMES.has(hostname) || hostname.endsWith(".local")) {
      return true;
    }
  } catch {
    // Keep logging if URL parsing fails.
  }

  if (error.error_type === "console_error") {
    const normalized = error.message.toLowerCase();
    if (IGNORED_CONSOLE_PATTERNS.some((pattern) => normalized.includes(pattern))) {
      return true;
    }
  }

  // Filter third-party browser extensions/scripts (e.g., Grammarly, password managers)
  const stackLower = (error.stack || error.message).toLowerCase();
  if (THIRD_PARTY_STACK_PATTERNS.some((pattern) => stackLower.includes(pattern))) {
    return true;
  }

  // 2026-08-19: the structural version of the list above. Drops an error whose
  // THROWING frame is an extension content script or a rewriting proxy mirror,
  // regardless of what it says. Keyed on the first frame only, so a real bug in
  // our code stays logged even when an extension that wrapped window.fetch
  // appears further down its stack. See @/lib/thirdPartyOrigin.
  if (isThirdPartyOriginStack(error.stack || error.message)) {
    return true;
  }

  const messageLower = error.message.toLowerCase();
  if (THIRD_PARTY_MESSAGE_PATTERNS.some((pattern) => messageLower.includes(pattern))) {
    return true;
  }

  if (matchesChunkLoadPattern(messageLower)) {
    return true;
  }

  if (AUTH_RATE_LIMIT_MESSAGE_PATTERNS.some((pattern) => messageLower.includes(pattern))) {
    return true;
  }

  // Stackless "TypeError: Failed to fetch" — almost always a network blip
  // (user offline, flaky connection, CORS preflight failure from a third-
  // party script). Every fetch in our own code is wrapped in try/catch with
  // the endpoint name in the logged message (see runGenerate.ts and siblings
  // — e.g. "research-tracks fetch failed: ..."), so a bare stackless one
  // isn't from our code and isn't actionable.
  if (
    !error.stack &&
    (messageLower === "typeerror: failed to fetch" ||
      messageLower === "failed to fetch" ||
      // iOS Safari emits "Load failed" instead of "Failed to fetch" for the
      // same network-blip class. Mirror Sentry's filter (instrument.ts).
      messageLower === "typeerror: load failed" ||
      messageLower === "load failed" ||
      messageLower === "networkerror when attempting to fetch resource.")
  ) {
    return true;
  }

  // Host-suffixed network blips from Supabase auth-js etc. — e.g.
  // "TypeError: Failed to fetch (tnffhgpavnrjozjjiasz.supabase.co)".
  // The Supabase JS auth-js logs this via console.error when its background
  // session-refresh fetch fails on cellular dropouts / captive portals.
  // It's the same noise class as the bare variant above; mirror Sentry's
  // NOISY_THIRD_PARTY_HOSTS_RE in instrument.ts. (Andy 2026-05-08 — these
  // were leaking into /admin/client-errors despite Sentry filtering them.)
  //
  // 2026-05-11: dropped the `error_type === "console_error"` gate. The same
  // noise class was leaking as `error_type: "custom"` from
  // runGenerate.tsx:3061's top-level catch (`logClientError({error_type:
  // 'custom', message: errMsg, ...})` where errMsg ends up being
  // "Failed to fetch (tnffhgpavnrjozjjiasz.supabase.co)" verbatim). The
  // host-suffix pattern is itself strong enough signal — if the message
  // matches a known noisy third-party host, it's noise regardless of which
  // logging path it took to get here.
  if (NOISY_THIRD_PARTY_HOSTS_RE.test(messageLower)) {
    return true;
  }

  // Cross-origin "Script error." — the canonical browser-masked error when
  // a script from a different origin (browser extension, ad-blocker injection,
  // analytics tag without CORS headers) throws. Browsers strip filename/lineno
  // for security, leaving us with `{filename:"", lineno:0, colno:0}` and a
  // bare "Script error." message. There is literally no debuggable signal.
  // 2026-04-29 prod: an anonymous /generate visitor logged this with empty
  // metadata fields — useless noise.
  if (
    error.error_type === "console_error" &&
    messageLower === "script error." &&
    error.metadata !== undefined
  ) {
    const meta = error.metadata as Record<string, unknown>;
    const filename = String(meta.filename ?? "");
    const lineno = Number(meta.lineno ?? 0);
    const colno = Number(meta.colno ?? 0);
    if (filename === "" && lineno === 0 && colno === 0) {
      return true;
    }
  }

  // Opaque-stack recursion: Firefox-style stacks with many frames but zero
  // file references (e.g. `al@\nZk@\nal@\n...`) indicate code running outside
  // our origin — typically a browser extension or injected content script.
  // We can't act on it and the stack gives us nothing to debug.
  if (error.stack) {
    const stackLines = error.stack.split("\n").filter((line) => line.trim());
    const hasFileRef = stackLines.some((line) => /https?:\/\/|\.(js|ts|tsx|mjs)\b/i.test(line));
    if (!hasFileRef && stackLines.length >= 10) {
      return true;
    }
  }

  return false;
}

/**
 * Log an error to the database
 */
/** True when the value already matches the structured ErrorLog shape (vs a raw
 *  Error / string / Supabase-error that needs normalizing). */
function isErrorLogShape(x: unknown): x is ErrorLog {
  return (
    typeof x === "object" && x !== null &&
    "error_type" in x && "message" in x && "url" in x && "user_agent" in x
  );
}

export async function logClientError(error: ErrorLog): Promise<void>;
export async function logClientError(error: unknown, context: string): Promise<void>;
export async function logClientError(input: ErrorLog | unknown, context?: string): Promise<void> {
  // Normalize the (error, context) convention some callers use (e.g. the admin
  // payments hooks) into a proper ErrorLog. Previously the raw first arg lacked
  // error_type/url/user_agent (so the client_errors row was malformed) and the
  // context string was silently dropped — this fixes both.
  const error: ErrorLog = isErrorLogShape(input)
    ? input
    : {
        error_type: "custom",
        message:
          input instanceof Error ? input.message
          : typeof input === "string" ? input
          : input && typeof input === "object" && "message" in input && typeof (input as { message: unknown }).message === "string"
            ? (input as { message: string }).message
            : String(input),
        stack: input instanceof Error ? input.stack : undefined,
        url: typeof window !== "undefined" ? window.location.href : "",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        metadata: context ? { context } : undefined,
      };

  // Check if error tracking is enabled
  const enabled = await isErrorTrackingEnabled();
  if (!enabled) {
    // Silent skip - no console spam when disabled
    return;
  }

  if (shouldSkipClientError(error)) {
    return;
  }

  // Rate limiting
  if (errorCount >= ERROR_LOG_LIMIT) {
    return;
  }

  // Deduplication - don't log the same error multiple times
  const errorKey = `${error.error_type}:${error.message}:${error.stack?.substring(0, 100)}`;
  if (loggedErrors.has(errorKey)) {
    return;
  }

  loggedErrors.add(errorKey);
  errorCount++;

  try {
    // Use cached user ID to avoid network call on every error
    if (!userIdFetched) {
      userIdFetched = true;
      const { data: { user } } = await supabase.auth.getUser();
      cachedUserId = user?.id ?? null;
    }

    // Defensive .substring() — when an unhandled error captures via the
    // window.onerror / unhandledrejection paths, the error object can be
    // missing url / user_agent (e.g. error from a worker, or a synthesised
    // error created without those fields). Calling .substring on undefined
    // is what crashed the logger itself in 2026-05-07's Sentry trace
    // (errorLogger.ts:205:24). Coerce to string before slicing.
    function safeStr(v: string, max: number): string;
    function safeStr(v: unknown, max: number): string | null;
    function safeStr(v: unknown, max: number): string | null {
      return typeof v === "string" ? v.slice(0, max) : v == null ? null : String(v).slice(0, max);
    }

    const { error: insertError } = await supabase
      .from('client_errors')
      .insert([{
        error_type: error.error_type,
        message: safeStr(error.message, 1000),
        stack: safeStr(error.stack, 5000),
        url: safeStr(error.url, 500),
        user_agent: safeStr(error.user_agent, 500),
        user_id: cachedUserId || error.user_id,
        metadata: error.metadata as Json,
      }]);

    if (insertError) {
      // Use the raw console.error captured at module load — bypasses Sentry's
      // captureConsoleIntegration and our own console.error wrapper. The
      // alternative (naked console.error) creates a meta-error in Sentry with
      // zero debugging value: we already know "insert failed" from insertError
      // itself, and the original error that triggered this path is already
      // filtered as noise by shouldSkipClientError + Sentry beforeSend.
      // SPOTIVIBLY-WEB-V, fixed 2026-05-11.
      _rawConsoleError('[ErrorLogger] Failed to log error:', insertError);
    }
  } catch (e) {
    // Silent fail - don't throw errors from error logger.
    // Same reasoning as above — use the raw console.error so we surface to
    // DevTools without re-entering either pipeline.
    _rawConsoleError('[ErrorLogger] Exception while logging:', e);
  }
}

/**
 * Initialize global error handlers
 * Returns a cleanup function to remove all listeners
 */
export function initErrorLogger(): (() => void) | undefined {
  // Skip if already initialized (HMR / strict mode double-render)
  if ((window as unknown as Record<string, boolean>)._errorLoggerInitialized) {
    return () => {};
  }
  (window as unknown as Record<string, boolean>)._errorLoggerInitialized = true;

  // Capture console.error (wrap original)
  const originalConsoleError = console.error;
  let isLogging = false; // Re-entry guard to prevent infinite recursion
  console.error = function(...args: unknown[]) {
    // Call original console.error
    originalConsoleError.apply(console, args);

    // Prevent recursion: logClientError failure → console.error → logClientError → ...
    if (isLogging) return;
    isLogging = true;

    // Log to database
    const message = args.map(arg => {
      if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
      if (typeof arg === 'object') { try { return JSON.stringify(arg); } catch { return String(arg); } }
      return String(arg);
    }).join(' ');

    logClientError({
      error_type: 'console_error',
      message,
      url: window.location.href,
      user_agent: navigator.userAgent,
      metadata: {
        arguments: args.map(arg => {
          if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
          if (typeof arg === 'object') { try { return JSON.stringify(arg).substring(0, 200); } catch { return String(arg); } }
          return String(arg);
        }),
        timestamp: new Date().toISOString(),
      },
      // S16-AUD-050: .catch before .finally — a rejected logClientError here
      // would otherwise surface as an unhandledrejection (design-rules §12.6
      // rule 5). The isLogging re-entry guard already prevents recursion; this
      // is belt-and-braces so the wrapper never leaks its own failure.
    }).catch(() => { /* logging is best-effort */ }).finally(() => { isLogging = false; });
  };

  // Capture unhandled errors (window.onerror)
  const errorHandler = (event: ErrorEvent) => {
    logClientError({
      error_type: 'console_error',
      message: event.message,
      stack: event.error?.stack,
      url: window.location.href,
      user_agent: navigator.userAgent,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        timestamp: new Date().toISOString(),
      },
      // S16-AUD-078: fire-and-forget needs .catch (design-rules 12.6 rule 5).
    }).catch(() => { /* logging is best-effort */ });
  };
  window.addEventListener('error', errorHandler);

  // Capture unhandled promise rejections
  const rejectionHandler = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);
    const stack = reason instanceof Error ? reason.stack : undefined;

    logClientError({
      error_type: 'unhandled_rejection',
      message,
      stack,
      url: window.location.href,
      user_agent: navigator.userAgent,
      metadata: {
        promise: String(event.promise).substring(0, 200),
        timestamp: new Date().toISOString(),
      },
      // S16-AUD-078, and the dangerous half of it: without this .catch a
      // rejected logClientError RE-ENTERS window.unhandledrejection, which is
      // this very handler — the feedback loop design-rules 12.6 rule 5 exists
      // to prevent. The console.error wrapper above got its .catch in
      // S16-AUD-050; its two siblings, registered in the same initErrorLogger,
      // did not.
    }).catch(() => { /* logging is best-effort */ });
  };
  window.addEventListener('unhandledrejection', rejectionHandler);

  // Return cleanup function
  return () => {
    window.removeEventListener('error', errorHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
    console.error = originalConsoleError;
    (window as unknown as Record<string, boolean>)._errorLoggerInitialized = false;
  };
}


/**
 * Log a React error (for ErrorBoundary)
 */
export function logReactError(
  error: Error,
  componentStack: string
): void {
  logClientError({
    error_type: 'react_error',
    message: error.message,
    stack: error.stack,
    url: window.location.href,
    user_agent: navigator.userAgent,
    metadata: {
      componentStack: componentStack.substring(0, 2000),
      errorName: error.name,
      timestamp: new Date().toISOString(),
    },
    // S16-AUD-078: called from an ErrorBoundary, which is already the
    // last line of defence — a rejection escaping here would surface as an
    // unhandledrejection on top of the error being reported.
  }).catch(() => { /* logging is best-effort */ });
}

/**
 * Clear error tracking state (for testing/debugging)
 */
export function clearErrorTracking(): void {
  errorCount = 0;
  loggedErrors.clear();
  cachedUserId = null;
  userIdFetched = false;
}

